import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { lazy, useMemo, useState } from 'react';
import { analyticsTrack } from '@wayste/client-utils';
import { useFormContext } from 'react-hook-form';
import CollapsibleCard, { CollapsibleCardClosedActionButton, CollapsibleCardClosedSubHeader, CollapsibleOpenSubHeader, } from './CollapsibleCard';
import { RolloffDumpsterSizeCard } from './dumpster-size-card';
import { SizeGuideDialog } from './size-guide-dialog';
const DisabledOverlay = lazy(() => import('@wayste/sour-ui').then((module) => ({ default: module.DisabledOverlay })));
/**
 * SizeDetails component for roll-off dumpster size selection.
 * This is a pure presentational component that displays dumpster options
 * and handles selection. All pricing calculations should be done by the parent.
 *
 * Uses fixed field names (sourgum-site style):
 * - selectedDumpster: The selected dumpster size
 * - selectedMaterial: The selected material (for enabling/disabling)
 * - selectedWeightLimit: Auto-set from tonLimit
 * - selectedRentalPeriod: Auto-set default rental period
 * - dumpsterSectionOpen: Controls collapsible card open/close state
 */
export const SizeDetails = ({ dumpsterOptions, rentalPeriod, priceType = 'ton', formName = 'Order_Rolloff' }) => {
    const [showSizeGuide, setShowSizeGuide] = useState(false);
    const { watch, setValue } = useFormContext();
    const watchSelectedMaterial = watch('selectedMaterial');
    const watchSelectedDumpster = watch('selectedDumpster');
    const watchDumpsterSectionOpen = watch('dumpsterSectionOpen');
    const onDumpsterSelect = (dumpster) => {
        // Analytics tracking
        if (typeof window !== 'undefined' && window.dataLayer && !watchSelectedDumpster) {
            analyticsTrack({
                event: 'Order_Online_Event',
                serviceSegment: 'Rolloff',
                formName: formName,
                fieldName: 'Size_Selected',
                value: dumpster.size,
            });
        }
        setValue('selectedDumpster', dumpster);
        setValue('selectedWeightLimit', dumpster?.tonLimit);
        // Auto-set rental period to base period (day 0, price 0)
        setValue('selectedRentalPeriod', { days: rentalPeriod || 0, price: 0 });
        // Close the section after selection
        setValue('dumpsterSectionOpen', false);
        // Scroll to rental period section after selection
        setTimeout(() => {
            const section = document.getElementById('selectedRentalPeriod');
            section?.focus({ preventScroll: true });
            section?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'end',
            });
        }, 700);
    };
    const isOpen = useMemo(() => {
        const autoOpen = !!watchSelectedMaterial && !watchSelectedDumpster;
        return autoOpen || watchDumpsterSectionOpen;
    }, [watchSelectedMaterial, watchSelectedDumpster, watchDumpsterSectionOpen]);
    const ClosedSummary = useMemo(() => {
        if (!watchSelectedDumpster)
            return null;
        return (_jsx(CollapsibleCardClosedSubHeader, { children: _jsx("div", { className: "text-primary-foreground flex flex-col", children: `${watchSelectedDumpster.size} Cubic Yards` }) }));
    }, [watchSelectedDumpster]);
    const ClosedActionButton = watchSelectedDumpster ? (_jsx(CollapsibleCardClosedActionButton, { onClick: () => setValue('dumpsterSectionOpen', true), children: "Change" })) : null;
    const SubHeader = (_jsx(CollapsibleOpenSubHeader, { text: _jsxs("span", { className: "text-base font-normal leading-none", children: ["Dimensions may vary slightly.", ' ', _jsx("button", { type: "button", className: "link link--inline", onClick: () => setShowSizeGuide(true), children: "What size is right for you?" })] }) }));
    return (_jsx(DisabledOverlay, { active: !watchSelectedMaterial, tooltipMsg: "Please select a material first.", children: _jsxs(_Fragment, { children: [_jsx(CollapsibleCard, { isOpen: isOpen, header: "Select Size", subHeader: SubHeader, closedView: ClosedSummary, closedActionButton: ClosedActionButton, children: _jsx("div", { className: "flex flex-col gap-y-4 xl:grid xl:grid-cols-2 xl:gap-x-5 xl:gap-y-8", children: !dumpsterOptions || dumpsterOptions.length === 0 ? (_jsx("div", { className: "col-span-2 px-3 text-sm italic text-gray-600", children: "To select a size you need to select a material first." })) : (dumpsterOptions.map((dumpInfo) => (_jsx(RolloffDumpsterSizeCard, { size: dumpInfo, selectedSize: watchSelectedDumpster, price: dumpInfo.price, priceType: priceType, rentalPeriod: rentalPeriod, onSelect: () => onDumpsterSelect(dumpInfo) }, dumpInfo.size)))) }) }), _jsx(SizeGuideDialog, { open: showSizeGuide, onClose: () => setShowSizeGuide(false) })] }) }));
};
export default SizeDetails;
//# sourceMappingURL=size-details.js.map